import type { AlmanacCableConsumer } from 'providers/CableConsumer/types'

const TIMEOUT_MS = 30_000

export interface VoiceoverScriptUpdate {
  event: 'script_update'
  status: 'processing' | 'ready' | 'failed'
  script: string | null
  error: string | null
}

type SessionState = {
  status: 'processing' | 'ready' | 'failed'
  script: string
  error: string | null
  promise: Promise<string>
  subscription: { unsubscribe: () => void } | null
}

const sessions = new Map<string, SessionState>()

export const parseVoiceoverScriptUpdate = (data: unknown): VoiceoverScriptUpdate | null => {
  if (!data || typeof data !== 'object') return null

  const payload = data as Record<string, unknown>
  const status = payload.status
  if (status !== 'processing' && status !== 'ready' && status !== 'failed') return null
  if (payload.event && payload.event !== 'script_update') return null

  return {
    event: 'script_update',
    status,
    script: typeof payload.script === 'string' ? payload.script : null,
    error: typeof payload.error === 'string' ? payload.error : null,
  }
}

export const ensureVoiceoverScriptSession = (
  websocketConnection: AlmanacCableConsumer,
  token: string,
): SessionState => {
  const existing = sessions.get(token)
  if (existing) return existing

  let resolve!: (script: string) => void
  let reject!: (error: Error) => void
  const promise = new Promise<string>((res, rej) => {
    resolve = res
    reject = rej
  })

  const session: SessionState = {
    status: 'processing',
    script: '',
    error: null,
    promise,
    subscription: null,
  }
  sessions.set(token, session)

  const subscription = websocketConnection.subscriptions.create(
    { channel: 'VoiceoverScriptChannel', token },
    {
      received(data: unknown) {
        const update = parseVoiceoverScriptUpdate(data)
        if (!update) return

        if (update.status === 'ready') {
          session.status = 'ready'
          session.script = update.script ?? ''
          subscription.unsubscribe()
          session.subscription = null
          resolve(session.script)
          return
        }

        if (update.status === 'failed') {
          session.status = 'failed'
          session.error = update.error || 'Script generation failed'
          subscription.unsubscribe()
          session.subscription = null
          reject(new Error(session.error))
        }
      },
    },
  )

  session.subscription = subscription

  window.setTimeout(() => {
    if (session.status !== 'processing') return
    session.status = 'failed'
    session.error = 'Script generation timed out'
    subscription.unsubscribe()
    session.subscription = null
    reject(new Error(session.error))
  }, TIMEOUT_MS)

  return session
}

export const waitForVoiceoverScript = (
  websocketConnection: AlmanacCableConsumer,
  token: string,
  timeoutMs = TIMEOUT_MS,
): Promise<string> => {
  const session = ensureVoiceoverScriptSession(websocketConnection, token)

  if (session.status === 'ready') return Promise.resolve(session.script)
  if (session.status === 'failed') return Promise.reject(new Error(session.error || 'Script generation failed'))

  if (timeoutMs === TIMEOUT_MS) return session.promise

  return Promise.race([
    session.promise,
    new Promise<string>((_, reject) => {
      window.setTimeout(() => reject(new Error('Script generation timed out')), timeoutMs)
    }),
  ])
}

export const clearVoiceoverScriptSession = (token: string) => {
  const session = sessions.get(token)
  session?.subscription?.unsubscribe()
  sessions.delete(token)
}

export const getVoiceoverScriptSession = (token: string) => sessions.get(token)
